// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { ShieldCheck, CheckCircle, X, Clock, Image, ClipboardList } from 'lucide-react';

export default function SupervisorQueuePage() {
  const { user } = useAuth();
  const [queue, setQueue]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [artifacts, setArtifacts]   = useState([]);
  const [entries, setEntries]       = useState([]);
  const [decision, setDecision]     = useState('');
  const [notes, setNotes]           = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    const { data } = await supabase
      .from('grocery_cleaning_sessions')
      .select(`
        *,
        grocery_zones(zone_name, zone_icon, zone_color, zone_code),
        grocery_supervisor_approvals(id, requested_at, requested_by)
      `)
      .eq('session_status', 'AWAITING_APPROVAL')
      .order('created_at', { ascending: true });
    setQueue(data || []);
    setLoading(false);
  };

  const openSession = async (sess) => {
    setSelected(sess);
    setDecision('');
    setNotes('');
    const [{ data: arts }, { data: ents }] = await Promise.all([
      supabase.from('grocery_proof_artifacts').select('*').eq('session_id', sess.id),
      supabase.from('grocery_checklist_entries')
        .select('*, grocery_cleaning_tasks(task_name, task_description)')
        .eq('session_id', sess.id),
    ]);
    setArtifacts(arts || []);
    setEntries(ents || []);
  };

  const handleDecision = async () => {
    if (!decision || !selected) return;
    setProcessing(true);

    const isApproved = decision === 'APPROVED';
    const userId = user?.auth_user_id || user?.id;
    const now    = new Date().toISOString();

    await Promise.all([
      // Update session status
      supabase.from('grocery_cleaning_sessions').update({
        session_status:  isApproved ? 'APPROVED' : 'REJECTED',
        approved_at:     isApproved ? now : null,
        approved_by:     isApproved ? userId : null,
        rejected_at:     !isApproved ? now : null,
        rejected_by:     !isApproved ? userId : null,
        supervisor_notes: notes || null,
        updated_at:      now,
      }).eq('id', selected.id),
      // Update approval record
      supabase.from('grocery_supervisor_approvals').update({
        decision:    isApproved ? 'APPROVED' : 'REJECTED',
        reviewed_at: now,
        reviewed_by: userId,
        notes:       notes || null,
      }).eq('session_id', selected.id),
    ]);

    setSelected(null);
    fetchQueue();
    setProcessing(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Approval Queue</h1>
          <p className="page-subtitle">
            {queue.length} session{queue.length !== 1 ? 's' : ''} awaiting supervisor sign-off
          </p>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: queue.length > 0 ? '#EDE9FE' : '#D1FAE5',
          color: queue.length > 0 ? '#7C3AED' : '#059669',
          padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700,
        }}>
          <ShieldCheck size={18} />
          {queue.length > 0 ? `${queue.length} Pending` : 'Queue Clear'}
        </div>
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="modal-content" style={{ maxWidth: 620, maxHeight: '92vh' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: selected.grocery_zones?.zone_color + '20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                }}>
                  {selected.grocery_zones?.zone_icon || '🧹'}
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#134E4A' }}>
                    {selected.grocery_zones?.zone_name}
                  </div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>
                    Completed {new Date(selected.completed_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8' }}>
                <X size={20} />
              </button>
            </div>

            {/* Score */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16,
              background: '#F0FDF9', borderRadius: 12, padding: '12px 16px', marginBottom: 16,
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: selected.score_pct >= 80 ? '#10B981' : '#F59E0B' }}>
                  {selected.score_pct || 0}%
                </div>
                <div style={{ fontSize: 10, color: '#94A3B8', fontWeight: 600 }}>SCORE</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 10, background: '#CCFBF1', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${selected.score_pct || 0}%`,
                    background: selected.score_pct >= 80 ? '#10B981' : '#F59E0B',
                    borderRadius: 5, transition: 'width 0.5s',
                  }} />
                </div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>
                  {entries.filter(e => e.completed).length}/{entries.length} tasks completed
                </div>
              </div>
            </div>

            {/* Checklist Entries */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <ClipboardList size={16} color="#0F766E" />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#134E4A' }}>Task Checklist</span>
              </div>
              {entries.map(e => (
                <div key={e.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                  borderBottom: '1px solid #F0FDF9',
                }}>
                  {e.completed
                    ? <CheckCircle size={16} color="#10B981" style={{ flexShrink: 0 }} />
                    : <X size={16} color="#EF4444" style={{ flexShrink: 0 }} />
                  }
                  <span style={{ fontSize: 13, color: e.completed ? '#134E4A' : '#94A3B8', fontWeight: e.completed ? 600 : 400 }}>
                    {e.grocery_cleaning_tasks?.task_name || 'Task'}
                  </span>
                </div>
              ))}
            </div>

            {/* Photo Artifacts */}
            {artifacts.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Image size={16} color="#0F766E" />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#134E4A' }}>Photo Documentation</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {artifacts.map(a => (
                    <div key={a.id} style={{ position: 'relative' }}>
                      {a.storage_url ? (
                        <img src={a.storage_url} alt={a.proof_type} style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 10 }} />
                      ) : (
                        <div style={{ width: '100%', height: 140, background: '#F0FDF9', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8', fontSize: 12 }}>
                          No photo
                        </div>
                      )}
                      <div style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(0,0,0,0.6)', borderRadius: 6, padding: '2px 8px', fontSize: 10, color: '#fff', fontWeight: 600, textTransform: 'uppercase' }}>
                        {a.proof_type.replace(/_/g, ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Supervisor Notes */}
            {selected.supervisor_notes && (
              <div style={{ background: '#FEF3C7', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#D97706', marginBottom: 4 }}>Janitor Notes</div>
                <div style={{ fontSize: 13, color: '#92400E' }}>{selected.supervisor_notes}</div>
              </div>
            )}

            {/* Decision */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', display: 'block', marginBottom: 6 }}>Supervisor Notes (optional)</label>
              <textarea
                className="input"
                rows={2}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add notes about your decision..."
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-danger btn-lg"
                style={{ flex: 1 }}
                onClick={() => { setDecision('REJECTED'); handleDecision(); }}
                disabled={processing}
              >
                <X size={18} />
                Reject
              </button>
              <button
                className="btn btn-success btn-lg"
                style={{ flex: 1 }}
                onClick={() => { setDecision('APPROVED'); handleDecision(); }}
                disabled={processing}
              >
                <CheckCircle size={18} />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="page-body">
        {queue.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '64px 0' }}>
            <ShieldCheck size={48} style={{ margin: '0 auto 16px', display: 'block', color: '#10B981' }} />
            <div style={{ fontSize: 18, fontWeight: 700, color: '#134E4A', marginBottom: 8 }}>All clear!</div>
            <div style={{ color: '#94A3B8' }}>No sessions waiting for approval.</div>
          </div>
        ) : (
          queue.map(sess => (
            <div
              key={sess.id}
              className="card"
              onClick={() => openSession(sess)}
              style={{ cursor: 'pointer', borderLeft: '4px solid #8B5CF6', display: 'flex', alignItems: 'center', gap: 16 }}
            >
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: sess.grocery_zones?.zone_color + '20',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0,
              }}>
                {sess.grocery_zones?.zone_icon || '🧹'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#134E4A', marginBottom: 4 }}>
                  {sess.grocery_zones?.zone_name || 'Unknown Zone'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={12} />
                    Completed {new Date(sess.completed_at).toLocaleString()}
                  </span>
                  {sess.score_pct != null && (
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: sess.score_pct >= 80 ? '#10B981' : '#F59E0B',
                      background: sess.score_pct >= 80 ? '#D1FAE5' : '#FEF3C7',
                      padding: '2px 8px', borderRadius: 6,
                    }}>
                      {sess.score_pct}% score
                    </span>
                  )}
                </div>
              </div>
              <div style={{ background: '#EDE9FE', color: '#7C3AED', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                Review →
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
